import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { db } from '../services/firebase';
import { collection, getDocs, query } from 'firebase/firestore';
import { ExternalLink, Github, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Project {
    id: string;
    title: string;
    description: string;
    technologies: string[];
    github?: string;
    demo?: string;
    image?: string;
    category: 'core' | 'side';
    order: number;
}

const ProjectsPage: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                // Temporarily removing orderBy to debug if documents are missing the 'order' field
                const q = query(collection(db, 'projects'));
                const snapshot = await getDocs(q);
                const data = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                })) as Project[];

                // Sort by order ascending
                data.sort((a, b) => (a.order || 0) - (b.order || 0));

                setProjects(data);
            } catch (err) {
                console.error('Failed to fetch projects:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProjects();
    }, []);

    const coreProjects = projects.filter((p) => p.category === 'core');
    const sideProjects = projects.filter((p) => p.category === 'side');

    const ProjectItem = ({ project, index }: { project: Project; index: number }) => (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
        >
            <Link
                to={`/projects/${project.id}`}
                className="group block py-6 screen-line-after"
            >
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                        <h3 className="text-base font-semibold text-foreground group-hover:opacity-80 transition-opacity">
                            {project.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            {project.description}
                        </p>
                        <div className="flex flex-wrap gap-2 pt-1">
                            {project.technologies.map((tech) => (
                                <span
                                    key={tech}
                                    className="px-2 py-0.5 text-xs font-mono text-muted-foreground border border-border rounded"
                                >
                                    {tech}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                        {project.github && (
                            <span
                                onClick={(e) => {
                                    e.preventDefault();
                                    window.open(project.github, '_blank');
                                }}
                                className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                                aria-label="GitHub"
                            >
                                <Github size={16} />
                            </span>
                        )}
                        {project.demo && (
                            <span
                                onClick={(e) => {
                                    e.preventDefault();
                                    window.open(project.demo, '_blank');
                                }}
                                className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                                aria-label="Demo"
                            >
                                <ExternalLink size={16} />
                            </span>
                        )}
                        <ArrowRight size={14} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                </div>
            </Link>
        </motion.div>
    );

    if (loading) {
        return (
            <div className="mx-auto max-w-3xl md:border-x px-4 py-24">
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground" />
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-3xl md:border-x">
            <div className="px-4 py-12">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <h1 className="text-3xl font-bold text-foreground mb-2">Projects</h1>
                    <p className="text-sm text-muted-foreground mb-12">
                        Things I've built and shipped.
                    </p>
                </motion.div>

                {coreProjects.length > 0 && (
                    <div className="mb-12">
                        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                            Built to Last
                        </h2>
                        {coreProjects.map((project, i) => (
                            <ProjectItem key={project.id} project={project} index={i} />
                        ))}
                    </div>
                )}

                {sideProjects.length > 0 && (
                    <div>
                        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                            Side Projects
                        </h2>
                        {sideProjects.map((project, i) => (
                            <ProjectItem key={project.id} project={project} index={i} />
                        ))}
                    </div>
                )}

                {projects.length === 0 && (
                    <p className="text-sm text-muted-foreground py-12 text-center">
                        No projects yet. Add some from the admin panel.
                    </p>
                )}
            </div>
        </div>
    );
};

export default ProjectsPage;
